// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/settings/BillingInfoPage.tsx
//
// Phase 1 Task 49 — uadmin billing info settings.
// PATCH /api/v1/users/me/billing-info — Tier A sensitive (requires fresh perms
// per API-CONTRACTS L3517). On 401 AUTH_FRESH_PERMS_REQUIRED / 428, FE forces
// a `/auth/refresh` and retries once.
//
// Per CONTEXT.md / API-CONTRACTS L3508: businessName, taxId, billingEmail,
// billingAddress { line1, city, postalCode, countryCode }.

import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  useAuth,
  SettingsLayout,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

interface BillingForm {
  businessName: string;
  taxId: string;
  billingEmail: string;
  billingAddress: {
    line1: string;
    city: string;
    postalCode: string;
    country: string; // 2-letter ISO 3166 code (mapped to API `countryCode`)
  };
}

const INITIAL: BillingForm = {
  businessName: '',
  taxId: '',
  billingEmail: '',
  billingAddress: { line1: '', city: '', postalCode: '', country: 'VN' },
};

type LooseClient = {
  PATCH: (p: string, init?: { body?: unknown }) => Promise<{
    data?: unknown;
    error?: unknown;
    response: Response;
  }>;
};

const FRESH_PERM_ERROR_CODES = new Set([
  'AUTH_FRESH_PERMS_REQUIRED',
  'AUTH_PERM_VERSION_STALE',
]);

export function BillingInfoPage(): JSX.Element {
  const { t } = useTranslation('settings');
  const refresh = useAuth((s) => s.refresh);
  const user = useAuth((s) => s.user);
  const fetchMe = useAuth((s) => s.fetchMe);

  const [form, setForm] = useState<BillingForm>(INITIAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // R3-10 (2026-05-16): hydrate from user.billingInfo.
  useEffect(() => {
    const b = user?.billingInfo;
    if (!b) return;
    setForm({
      businessName: b.legalName ?? '',
      taxId: b.taxId ?? '',
      billingEmail: b.billingEmail ?? '',
      billingAddress: {
        line1: b.addressLine1 ?? '',
        city: b.city ?? '',
        postalCode: b.postalCode ?? '',
        country: b.country ?? 'VN',
      },
    });
  }, [user]);

  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => setSuccess(false), 4000);
    return () => window.clearTimeout(t);
  }, [success]);

  const setField = <K extends keyof BillingForm>(
    k: K,
    v: BillingForm[K],
  ): void => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };
  const setAddr = <K extends keyof BillingForm['billingAddress']>(
    k: K,
    v: BillingForm['billingAddress'][K],
  ): void => {
    setForm((prev) => ({
      ...prev,
      billingAddress: { ...prev.billingAddress, [k]: v },
    }));
  };

  // R3-10: switch to /auth/me alias — legacy /users/me/billing-info is 404
  // on sandbox.
  const buildBody = (): Record<string, unknown> => {
    const billing: Record<string, unknown> = {};
    if (form.businessName) billing['legalName'] = form.businessName;
    if (form.taxId) billing['taxId'] = form.taxId;
    if (form.billingEmail) billing['billingEmail'] = form.billingEmail;
    const a = form.billingAddress;
    if (a.line1) billing['addressLine1'] = a.line1;
    if (a.city) billing['city'] = a.city;
    if (a.postalCode) billing['postalCode'] = a.postalCode;
    if (a.country) billing['country'] = a.country.toUpperCase();
    return { billing };
  };

  const sendPatch = async (
    body: Record<string, unknown>,
  ): Promise<Response> => {
    const client = apiClient() as unknown as LooseClient;
    const { response } = await client.PATCH(
      '/api/v1/auth/me',
      { body },
    );
    return response;
  };

  const handleSubmit = async (
    ev: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    ev.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const body = buildBody();
      if (Object.keys(body).length === 0) {
        setError(t('billingInfo.errors.needAtLeastOne'));
        return;
      }
      let response = await sendPatch(body);

      // Tier A — handle 401 perm-stale + retry once after /auth/refresh.
      if (response.status === 401 || response.status === 428) {
        let code = `HTTP_${response.status}`;
        try {
          const env = (await response.clone().json()) as
            | { error?: { code?: string } }
            | undefined;
          if (env?.error?.code) code = env.error.code;
        } catch {
          // ignore body parse error
        }
        if (FRESH_PERM_ERROR_CODES.has(code) || response.status === 428) {
          await refresh();
          response = await sendPatch(body);
        }
      }

      if (!response.ok) {
        let code = `HTTP_${response.status}`;
        try {
          const env = (await response.clone().json()) as
            | { error?: { code?: string } }
            | undefined;
          if (env?.error?.code) code = env.error.code;
        } catch {
          // ignore
        }
        setError(code);
        return;
      }
      setSuccess(true);
      await fetchMe();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'BILLING_UPDATE_FAILED');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsLayout portal="admin" activeTab="billing">
      <Card>
        <CardHeader>
          <CardTitle>{t('billingInfo.card.title')}</CardTitle>
          <CardDescription>{t('billingInfo.card.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={handleSubmit} data-testid="billing-form">
            <FormField>
              <FormLabel htmlFor="businessName">{t('billingInfo.fields.businessName')}</FormLabel>
              <FormControl>
                <Input
                  id="businessName"
                  value={form.businessName}
                  onChange={(e) => setField('businessName', e.target.value)}
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="taxId">{t('billingInfo.fields.taxId')}</FormLabel>
              <FormControl>
                <Input
                  id="taxId"
                  value={form.taxId}
                  onChange={(e) => setField('taxId', e.target.value)}
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="billingEmail">{t('billingInfo.fields.billingEmail')}</FormLabel>
              <FormControl>
                <Input
                  id="billingEmail"
                  type="email"
                  value={form.billingEmail}
                  onChange={(e) => setField('billingEmail', e.target.value)}
                />
              </FormControl>
            </FormField>

            <fieldset className="mt-6 space-y-4 rounded-md border border-pw-border p-4">
              <legend className="px-2 text-sm font-medium text-pw-text">
                {t('billingInfo.address.legend')}
              </legend>

              <FormField>
                <FormLabel htmlFor="line1">{t('billingInfo.fields.line1')}</FormLabel>
                <FormControl>
                  <Input
                    id="line1"
                    value={form.billingAddress.line1}
                    onChange={(e) => setAddr('line1', e.target.value)}
                    autoComplete="address-line1"
                  />
                </FormControl>
              </FormField>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField>
                  <FormLabel htmlFor="city">{t('billingInfo.fields.city')}</FormLabel>
                  <FormControl>
                    <Input
                      id="city"
                      value={form.billingAddress.city}
                      onChange={(e) => setAddr('city', e.target.value)}
                      autoComplete="address-level2"
                    />
                  </FormControl>
                </FormField>
                <FormField>
                  <FormLabel htmlFor="postalCode">{t('billingInfo.fields.postalCode')}</FormLabel>
                  <FormControl>
                    <Input
                      id="postalCode"
                      value={form.billingAddress.postalCode}
                      onChange={(e) => setAddr('postalCode', e.target.value)}
                      autoComplete="postal-code"
                    />
                  </FormControl>
                </FormField>
              </div>

              <FormField>
                <FormLabel htmlFor="country">{t('billingInfo.fields.country')}</FormLabel>
                <FormControl>
                  <Select
                    value={form.billingAddress.country}
                    onValueChange={(value) => setAddr('country', value)}
                  >
                    <SelectTrigger id="country">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="VN">{t('billingInfo.countries.VN')}</SelectItem>
                      <SelectItem value="US">{t('billingInfo.countries.US')}</SelectItem>
                      <SelectItem value="SG">{t('billingInfo.countries.SG')}</SelectItem>
                      <SelectItem value="TH">{t('billingInfo.countries.TH')}</SelectItem>
                      <SelectItem value="JP">{t('billingInfo.countries.JP')}</SelectItem>
                      <SelectItem value="GB">{t('billingInfo.countries.GB')}</SelectItem>
                      <SelectItem value="FR">{t('billingInfo.countries.FR')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
              </FormField>
            </fieldset>

            {error ? (
              <FormMessage data-testid="billing-error">{error}</FormMessage>
            ) : null}
            {success ? (
              <p
                className="text-xs font-medium text-green-600 dark:text-green-300"
                data-testid="billing-success"
              >
                {t('billingInfo.success')}
              </p>
            ) : null}

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={saving} data-testid="billing-submit">
                {saving ? t('billingInfo.buttons.saving') : t('billingInfo.buttons.save')}
              </Button>
            </div>
          </Form>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
